import { Suspense } from "react";
import { Outlet } from "react-router-dom";
import { Container, Spinner } from "bizpandit-ui";
import { Sidebar } from "@/shared/layout/Sidebar";
import  Navbar  from "@/shared/layout/Navbar";
import { useUiStore } from "@/stores/uiStore";
import { useAuthStore } from "@/stores/authStore";
import { JobHealthBanner } from "@/features/jobHealth/components/JobHealthBanner";
import { AttendanceCheckInBanner } from "@/features/hr/components/AttendanceCheckInBanner";
import { cn } from "@/lib/utils";

/**
 * Primary authenticated shell: sidebar + navbar + routed content.
 * Sidebar/Navbar are app-owned (see shared/layout) — bizpandit-ui-1 only
 * supplies UI primitives, not page-level chrome.
 */
export function AppLayout() {
  const isSidebarCollapsed = useUiStore((s) => s.isSidebarCollapsed);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const user = useAuthStore((s) => s.user);

  return (
    <div className="min-h-screen bg-surface">
      <Sidebar collapsed={isSidebarCollapsed} onToggle={toggleSidebar} />

      <div
        className={cn(
          "flex min-h-screen min-w-0 flex-col overflow-x-hidden transition-all duration-300",
          isSidebarCollapsed ? "ml-16" : "ml-[200px]",
        )}
      >
        <Navbar user={user} onToggleSidebar={toggleSidebar} />

        {/*
          The width cap is the library's `Container size="page"`, not a
          hand-rolled max-width here — look-and-feel decisions belong in the
          design system, where a second consumer inherits them.

          `pad="none"` because page roots already set their own padding; the
          shell used to add a second inset around it, and its own value was
          `p-4 sm:p-6 lg:p-4` — larger on a tablet than on a desktop.
        */}
        <main className="flex-1 pb-10">
          <Container size="page" pad="none">
            {/*
              Above the page, not inside it: a failing nightly job is a
              condition of the system rather than of whichever screen happens
              to be open, and it needs to be seen from wherever the person is.

              It renders nothing for a viewer without `audit:read` — the query
              403s and the banner returns null — so this costs the rest of the
              staff a single failed request per session and shows them nothing.
            */}
            <JobHealthBanner />
            {/*
              The attendance capture (BA §3.1a): the login IS the check-in.
              Renders nothing for a user with no employee record.
            */}
            <AttendanceCheckInBanner />
            <Suspense
              fallback={
                <div className="flex h-64 items-center justify-center">
                  <Spinner size="lg" />
                </div>
              }
            >
              <Outlet />
            </Suspense>
          </Container>
        </main>
      </div>
    </div>
  );
}
